<template>
    <div class="isolate bg-white px-6 py-24 sm:py-32 lg:px-8 relative">
        <div class="mx-auto max-w-7xl text-center">
            <span class="text-sm font-semibold tracking-wide text-primary-600 uppercase">Contact us</span>
            <h2 class="mt-2 text-3xl font-bold tracking-tight text-balance text-gray-900 sm:text-4xl">Have a question or
                want to work together?</h2>
            <p class="mt-2 text-lg/8 text-gray-600">
                We’d love to hear from you. Just fill out the form below and
                we’ll get back to you as soon as possible.
            </p>
        </div>
        <form @submit.prevent="submitContactRequest"
            class="mx-auto mt-16 max-w-5xl rounded-2xl border border-gray-100 bg-white p-8 shadow-soft sm:mt-20 sm:p-10">
            <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                <div>
                    <label for="first-name" class="block text-sm/6 font-semibold text-gray-900">First name</label>
                    <div class="mt-2.5">
                        <input v-model="form.firstName" type="text" name="first-name" id="first-name"
                            autocomplete="given-name" required
                            class="block w-full rounded-xl bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600" />
                    </div>
                </div>
                <div>
                    <label for="last-name" class="block text-sm/6 font-semibold text-gray-900">Last name</label>
                    <div class="mt-2.5">
                        <input v-model="form.lastName" type="text" name="last-name" id="last-name"
                            autocomplete="family-name" required
                            class="block w-full rounded-xl bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <label for="email" class="block text-sm/6 font-semibold text-gray-900">Email</label>
                    <div class="mt-2.5">
                        <input v-model="form.email" type="email" name="email" id="email" autocomplete="email"
                            required
                            class="block w-full rounded-xl bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <label for="phone-number" class="block text-sm/6 font-semibold text-gray-900">Phone number</label>
                    <div class="mt-2.5">
                        <div
                            class="flex rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-primary-600">
                            <div class="grid shrink-0 grid-cols-1 focus-within:relative">
                                <select id="country" name="country" autocomplete="country" aria-label="Country"
                                    class="col-start-1 row-start-1 w-full appearance-none rounded-xl py-2 pr-7 pl-3.5 text-base text-gray-500 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600 sm:text-sm/6">
                                    <option>US</option>
                                    <option>CA</option>
                                    <option>EU</option>
                                </select>
                                <ChevronDownIcon
                                    class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                    aria-hidden="true" />
                            </div>
                            <input v-model="form.phoneNumber" type="text" name="phone-number" id="phone-number"
                                class="block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                                placeholder="123-456-7890" />
                        </div>
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <label for="message" class="block text-sm/6 font-semibold text-gray-900">Message</label>
                    <div class="mt-2.5">
                        <textarea v-model="form.message" name="message" id="message" rows="4" required
                            class="block w-full rounded-xl bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600" />
                    </div>
                </div>
                <SwitchGroup as="div" class="flex gap-x-4 sm:col-span-2">
                    <div class="flex h-6 items-center">
                        <Switch v-model="agreed"
                            :class="[agreed ? 'bg-primary-600' : 'bg-gray-200', 'flex w-8 flex-none cursor-pointer rounded-full p-px ring-1 ring-gray-900/5 transition-colors duration-200 ease-in-out ring-inset focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600']">
                            <span class="sr-only">Agree to policies</span>
                            <span aria-hidden="true"
                                :class="[agreed ? 'translate-x-3.5' : 'translate-x-0', 'size-4 transform rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition duration-200 ease-in-out']" />
                        </Switch>
                    </div>
                    <SwitchLabel class="text-sm/6 text-gray-600">
                        By selecting this, you agree to our
                        {{ ' ' }}
                        <router-link to="/privacy-policy" class="font-semibold text-primary-600">privacy&nbsp;policy</router-link>.
                    </SwitchLabel>
                </SwitchGroup>
            </div>
            <p v-if="submitted" class="mt-6 text-sm font-semibold text-primary-600">Thanks for reaching out! We'll get
                back to you soon.</p>
            <p v-if="submitError" class="mt-6 text-sm font-semibold text-red-600">Something went wrong. Please try
                again later.</p>
            <div class="mt-10">
                <button type="submit" :disabled="!agreed || submitting"
                    class="block w-full rounded-xl bg-primary-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-xs transition-colors hover:bg-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:opacity-50">
                    {{ submitting ? 'Sending...' : "Let's talk" }}</button>
            </div>

            <SocialMediaSection />
        </form>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { ChevronDownIcon } from '@heroicons/vue/16/solid'
import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import SocialMediaSection from '@/components/sections/SocialMediaSection.vue'
import { contactRequestService } from '@/services/contactRequestService'

const agreed = ref(false)
const submitting = ref(false)
const submitted = ref(false)
const submitError = ref(false)

const form = ref({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
})

const submitContactRequest = async () => {
    submitting.value = true
    submitted.value = false
    submitError.value = false

    try {
        await contactRequestService(form.value)
        submitted.value = true
        form.value = { firstName: '', lastName: '', email: '', phoneNumber: '', message: '' }
        agreed.value = false
    } catch (error) {
        console.error('Error submitting contact request:', error)
        submitError.value = true
    } finally {
        submitting.value = false
    }
}
</script>